<script>
import DomHandler from '@/common/DOMUtils.js';
import validationMixin from '@/common/validationMixin.js';

export default {
    name: 'EditableCellBase',
    mixins: [validationMixin],
    props: {
        name: {
            type: String,
            required: true
        },
        formatter: {
            type: Function,
            default: null
        },
        editable: {
            type: Boolean,
            default: true
        },
        focusable: {
            type: Boolean,
            default: true
        },
        isFocused: {
            type: Boolean,
            default: false
        },
        validatorRules: {
            type: Array,
            default: () => []
        },
        rowIndex: {
            type: Number,
            default: null
        },
        columnIndex: {
            type: Number,
            default: null
        },
        rowKey: {
            type: [String, Number],
            default: null
        },
        columnKey: {
            type: String,
            default: null
        },
        frozen: {
            type: Boolean,
            default: false
        },
        alignFrozen: {
            type: String,
            default: 'left',
            validator: value => ['left', 'right'].includes(value)
        },
        cellStyle: {
            type: Object,
            default: () => {}
        },
        cellStyleCallback: {
            type: Function,
            default: null
        },
        cellClass: {
            type: String,
            default: ''
        },
        cellClassCallback: {
            type: Function,
            default: null
        },
        isCellEditableCallback: {
            type: Function,
            default: null
        },
        item: {
            type: Object,
            required: true
        },
        isAppendSection: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isEditing: false,
            invalid: false,
            styleObject: {}
        };
    },
    watch: {
        isFocused(newVal) {
            if (newVal) {
                this.$nextTick(() => {
                    if (!this.isEditing && this.$refs.displayRef) {
                        this.$refs.displayRef.focus({ preventScroll: true });
                    }
                });
            }
        }
    },
    computed: {
        editableCell() {
            let editable = this.editable;

            if (editable && this.isCellEditableCallback) {
                editable = this.isCellEditableCallback(this.createEventData(this.value));
            }

            return editable;
        },
        valueFormatted() {
            return this.formatter ? this.formatter(this.value) : this.value;
        },
        row() {
            return this.rowIndex;
        },
        column() {
            return this.columnIndex;
        },
        containerStyle() {
            let style = this.frozen ? [this.styleObject] : [];

            if (this.cellStyle) {
                style.push(this.cellStyle);
            }

            if (this.cellStyleCallback) {
                const renderStyle = this.cellStyleCallback(this.createEventData(this.value));

                if (renderStyle) {
                    if (Array.isArray(renderStyle)) {
                        style = style.concat(renderStyle);
                    } else {
                        style.push(renderStyle);
                    }
                }
            }

            return style;
        },
        containerClass() {
            const columnClass = 'md-editable-grid-cell-editable'; //text-right
            let renderClass = '';

            if (this.cellClassCallback) {
                renderClass = this.cellClassCallback(this.createEventData(this.value));
            }

            let frozenClass = '';
            if (this.frozen) {
                frozenClass = 'table-frozen-column';
            }

            return [
                this.cellClass ?? '',
                columnClass,
                renderClass,
                frozenClass,
                {
                    'md-editable-grid-cell-focus':
                        this.isFocused && !this.invalid && !this.isEditing,
                    'md-editable-grid-cell-focus-invalid':
                        this.isFocused && this.invalid && !this.isEditing,
                    'md-editable-grid-cell-invalid':
                        this.invalid && !this.isFocused && !this.isEditing,
                    'md-editable-grid-cell-readonly': !this.editableCell
                }
            ].filter(x => x.length !== 0);
        }
    },
    methods: {
        createEventData(val) {
            const event = {
                rowKey: this.rowKey,
                columnKey: this.columnKey,
                row: this.row,
                column: this.column,
                value: val,
                valid: !this.invalid,
                name: this.name
            };

            return event;
        },

        onCellDblClick() {
            this.$parentInstance.cellDblClick(this.createEventData(this.value));
        },

        setFocus() {
            if (this.focusable === undefined || this.focusable) {
                this.$emit('cellFocus', this.createEventData(this.value));
            }
        },

        requestEdit() {
            if (!this.editableCell) {
                this.onCellDblClick();
                return;
            }

            this.$emit('requestEdit', this.createEventData(this.value));
        },

        validateCell(val = null) {
            let state = true;
            const valueToCheck = val !== null ? val : String(this.value ?? '').trim();

            if (this.validatorRules) {
                const rules = {
                    [this.name]: this.validatorRules
                };
                state = this.validateFields(rules, { [this.name]: valueToCheck });
            }

            this.invalid = !state;
            if (this.isFocused) {
                this.$emit('validationState', { name: this.name, invalid: this.invalid });
            }

            return state;
        },

        updateStickyPosition() {
            if (this.frozen) {
                let align = this.alignFrozen;

                if (align === 'right') {
                    let right = 0;
                    let next = DomHandler.getNextElementSibling(
                        this.$el,
                        '[data-p-frozen-column="true"]'
                    );

                    if (next) {
                        right = DomHandler.getOuterWidth(next) + parseFloat(next.style.right || 0);
                    }

                    this.$set(this.styleObject, 'insetInlineEnd', right + 'px');
                } else {
                    let left = 0;
                    let prev = DomHandler.getPreviousElementSibling(
                        this.$el,
                        '[data-p-frozen-column="true"]'
                    );

                    if (prev) {
                        left = DomHandler.getOuterWidth(prev) + parseFloat(prev.style.left || 0);
                    }

                    this.$set(this.styleObject, 'insetInlineStart', left + 'px');
                }
            }
        },

        handleDisplayKeydown(event) {
            switch (event.key) {
                case 'Enter':
                    event.preventDefault();

                    if (this.editableCell) {
                        this.requestEdit();
                    } else {
                        this.$emit('nextCell');
                    }
                    break;

                case 'F2':
                    if (this.editableCell) {
                        event.preventDefault();
                        this.requestEdit();
                    }
                    break;

                case 'Tab':
                    event.preventDefault();
                    this.$emit(event.shiftKey ? 'prevCell' : 'nextCell');
                    break;

                case 'ArrowUp':
                    event.preventDefault();
                    this.$emit('upCell');
                    break;

                case 'ArrowDown':
                    event.preventDefault();
                    this.$emit('downCell');
                    break;

                case 'ArrowLeft':
                    event.preventDefault();
                    this.$emit('prevCell');
                    break;

                case 'ArrowRight':
                    event.preventDefault();
                    this.$emit('nextCell');
                    break;
                default:
                    if (
                        event.key.length === 1 &&
                        !event.ctrlKey &&
                        !event.metaKey &&
                        !event.altKey &&
                        this.editableCell
                    ) {
                        event.preventDefault();
                        this.startEdit(event.key);
                    }
            }
        },

        handleKeydown(event) {
            switch (event.key) {
                case 'Enter':
                    event.preventDefault();
                    this.finishEdit(event);

                    this.$emit('nextCell');
                    break;
                case 'Tab':
                    event.preventDefault();
                    this.finishEdit(event);

                    this.$emit(event.shiftKey ? 'prevCell' : 'nextCell');
                    break;
                case 'ArrowUp':
                    if (this.caretAtStart()) {
                        event.preventDefault();
                        this.finishEdit(event);

                        this.$emit('upCell');
                    }
                    break;
                case 'ArrowDown':
                    if (this.caretAtEnd()) {
                        event.preventDefault();
                        this.finishEdit(event);

                        this.$emit('downCell');
                    }
                    break;
                case 'ArrowLeft':
                    if (this.caretAtStart() && !this.isAllSelected()) {
                        event.preventDefault();
                        this.finishEdit(event);

                        this.$emit('prevCell');
                    }
                    break;
                case 'ArrowRight':
                    if (this.caretAtEnd() && !this.isAllSelected()) {
                        event.preventDefault();
                        this.finishEdit(event);

                        this.$emit('nextCell');
                    }
                    break;
                case 'Escape':
                    event.preventDefault();

                    this.isEditing = false;
                    break;
            }
        },

        caretAtStart() {
            const sel = window.getSelection();
            if (!sel.rangeCount) return false;

            const range = sel.getRangeAt(0);
            const el = this.$refs.inputRef;

            const clone = range.cloneRange();
            clone.selectNodeContents(el);
            clone.setEnd(range.startContainer, range.startOffset);

            return clone.toString().trim().length === 0;
        },

        caretAtEnd() {
            const sel = window.getSelection();
            if (!sel.rangeCount) return false;

            const range = sel.getRangeAt(0);
            const el = this.$refs.inputRef;

            const clone = range.cloneRange();
            clone.selectNodeContents(el);
            clone.setStart(range.endContainer, range.endOffset);

            return clone.toString().trim().length === 0;
        },

        moveCaretToEnd(el) {
            const range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false);

            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
        },

        inputContentFocus() {
            if (this.editable) {
                this.$nextTick(() => {
                    const el = this.$refs.inputRef;
                    const sel = window.getSelection();
                    const range = document.createRange();
                    range.selectNodeContents(el);

                    if (sel) {
                        sel.removeAllRanges();
                        sel.addRange(range);
                    }

                    el.focus();
                });
            }
        },

        displayContentFocus() {
            if (!this.editable) {
                this.$nextTick(() => {
                    const el = this.$refs.displayRef;
                    el.focus();
                });
            }
        },

        isAllSelected() {
            const sel = window.getSelection();
            if (!sel.rangeCount) return false;

            const range = sel.getRangeAt(0);
            const el = this.$refs.inputRef;

            // Compara el texto seleccionado con el texto total de la celda
            const selectedText = range.toString().trim();
            const totalText = el.innerText.trim();

            // Retorna verdadero si miden lo mismo y hay texto seleccionado
            return selectedText.length > 0 && selectedText.length === totalText.length;
        }
    },

    mounted() {
        if (this.frozen) {
            this.updateStickyPosition();
        }
    },

    updated() {
        if (this.frozen) {
            this.updateStickyPosition();
        }
    }
};
</script>
